import { x25519 } from '@noble/curves/ed25519';
import { uploadBlob, type BlobUploadProgress } from '../api/blobUpload';
import {
  decryptInfoShareAttachmentFile,
  decryptInfoShareAttachmentName,
  downloadInfoShareAttachment,
  encryptInfoShareAttachments,
} from './infoShareAttachments';
import { encryptManifest, decryptManifest, type InfoShareManifestPlaintext } from './infoShareFragment';
import {
  decodeBase64Url,
  randomBytes,
} from './primitives';
import { wrapKey, unwrapKey } from './wrappedKey';

export {
  decryptInfoShareAttachmentFile as decryptReplyAttachmentFile,
  decryptInfoShareAttachmentName as decryptReplyAttachmentName,
  downloadInfoShareAttachment as downloadReplyAttachment,
};

export function replyWrapContextAad(infoShareId: string, ownerUserId: string): string {
  return `shareweb:wrap:v1|reply|${infoShareId}|${ownerUserId}`;
}

export async function encryptGuestReply(input: {
  text: string;
  files?: File[];
  infoShareId: string;
  ownerUserId: string;
  replyPublicKey: Uint8Array;
  onProgress?: (progress: BlobUploadProgress) => void;
}): Promise<{
  wrappedContentKey: Uint8Array;
  manifestBlobId: string;
  attachmentCount: number;
  attachmentBytes: number;
  attachmentBlobIds: string[];
}> {
  const contentKey = randomBytes(32);
  const files = input.files ?? [];
  const encryptedFiles = await encryptInfoShareAttachments(contentKey, files, input.onProgress);

  const manifest: InfoShareManifestPlaintext = {
    text: input.text,
    attachments: encryptedFiles.attachments,
  };
  const manifestBlob = await encryptManifest(contentKey, manifest);
  const manifestBlobId = await uploadBlob(manifestBlob);
  const wrappedContentKey = await wrapKey(
    contentKey,
    input.replyPublicKey,
    replyWrapContextAad(input.infoShareId, input.ownerUserId),
  );

  return {
    wrappedContentKey,
    manifestBlobId,
    attachmentCount: encryptedFiles.attachments.length,
    attachmentBytes: encryptedFiles.attachmentBytes,
    attachmentBlobIds: encryptedFiles.attachmentBlobIds,
  };
}

export async function decryptOwnerReply(input: {
  wrappedContentKey: Uint8Array;
  manifestBlob: Uint8Array;
  replyPrivateKey: Uint8Array;
  infoShareId: string;
  ownerUserId: string;
}): Promise<{ manifest: InfoShareManifestPlaintext; contentKey: Uint8Array }> {
  const contentKey = await unwrapKey(
    input.wrappedContentKey,
    input.replyPrivateKey,
    replyWrapContextAad(input.infoShareId, input.ownerUserId),
  );
  const manifest = await decryptManifest(contentKey, input.manifestBlob);
  return { manifest, contentKey };
}

export function decodeReplyPublicKey(base64Url: string): Uint8Array {
  const key = decodeBase64Url(base64Url);
  if (key.length !== 32) {
    throw new Error('invalid_reply_public_key');
  }
  return key;
}

export function generateReplyKeyPair(): { privateKey: Uint8Array; publicKey: Uint8Array } {
  const privateKey = x25519.utils.randomPrivateKey();
  return { privateKey, publicKey: x25519.getPublicKey(privateKey) };
}
